type NodeLike = {
    nodeId: string;
    parentNodeId: string | null;
    weight: number | string | null | undefined;
    isDeleted?: boolean;
};

export type WbsWeightMismatch =
    | { kind: 'ROOT_SUM'; expected: number; actual: number | null; count: number }
    | {
          kind: 'PARENT_CHILD_SUM';
          nodeId: string;
          parentWeight: number | null;
          childSum: number | null;
          childCount: number;
      };

const toNumOrNull = (v: any): number | null => {
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

export function validateWbsWeights(
    items: NodeLike[],
    opts?: { rootSum?: number; eps?: number },
): { ok: boolean; mismatches: WbsWeightMismatch[] } {
    const rootSum = opts?.rootSum ?? 100;
    const eps = opts?.eps ?? 1e-6;

    const alive = items.filter((x) => !x.isDeleted);
    const byParent = new Map<string | null, NodeLike[]>();
    for (const it of alive) {
        const pid = it.parentNodeId ?? null;
        const arr = byParent.get(pid) ?? [];
        arr.push(it);
        byParent.set(pid, arr);
    }

    const byNodeId = new Map(alive.map((x) => [x.nodeId, x] as const));
    const mismatches: WbsWeightMismatch[] = [];

    // 1) parent == sum(children)
    for (const [parentId, kids] of byParent.entries()) {
        if (!parentId) continue;
        if (kids.length === 0) continue;

        const parent = byNodeId.get(parentId);
        if (!parent) continue;

        const pW = toNumOrNull(parent.weight);
        const childWs = kids.map((k) => toNumOrNull(k.weight));

        if (pW === null || childWs.some((w) => w === null)) {
            mismatches.push({
                kind: 'PARENT_CHILD_SUM',
                nodeId: parentId,
                parentWeight: pW,
                childSum: null,
                childCount: kids.length,
            });
            continue;
        }

        const sum = (childWs as number[]).reduce((acc, cur) => acc + cur, 0);
        if (Math.abs(pW - sum) > eps) {
            mismatches.push({
                kind: 'PARENT_CHILD_SUM',
                nodeId: parentId,
                parentWeight: pW,
                childSum: sum,
                childCount: kids.length,
            });
        }
    }

    // 2) root sum == 100
    const rootKids = byParent.get(null) ?? [];
    if (rootKids.length > 0) {
        const rootWs = rootKids.map((k) => toNumOrNull(k.weight));
        if (rootWs.some((w) => w === null)) {
            mismatches.push({ kind: 'ROOT_SUM', expected: rootSum, actual: null, count: rootKids.length });
        } else {
            const sum = (rootWs as number[]).reduce((acc, cur) => acc + cur, 0);
            if (Math.abs(rootSum - sum) > eps) {
                mismatches.push({ kind: 'ROOT_SUM', expected: rootSum, actual: sum, count: rootKids.length });
            }
        }
    }

    return { ok: mismatches.length === 0, mismatches };
}
